import type {
  HelpArticleSummary,
  HelpCategory,
  HelpIndex,
  HelpPortalIndex,
  HelpPortalKey,
} from './types'

export const HELP_ROOT = '/help'

export function helpArticlePath(portal: HelpPortalKey, articleId: number) {
  return `${HELP_ROOT}/a/${portal}/${articleId}`
}

export function helpCategoryPath(portal: HelpPortalKey, categoryId: number) {
  return `${HELP_ROOT}/c/${portal}/${categoryId}`
}

// The internal portal is shown as one collection, like "cleverlohn Intern" on
// the old help center; this is its page.
export const PORTAL_COLLECTION_ID = 'all'

export function helpPortalPath(portal: HelpPortalKey) {
  return `${HELP_ROOT}/c/${portal}/${PORTAL_COLLECTION_ID}`
}

export function helpSearchPath(query: string) {
  return `${HELP_ROOT}?q=${encodeURIComponent(query)}`
}

export interface HelpCategoryNode extends HelpCategory {
  children: HelpCategoryNode[]
  articles: HelpArticleSummary[]
  // Articles in this category and everything below it.
  articleCount: number
}

function byPosition<T extends { position: number; id: number }>(a: T, b: T) {
  return a.position - b.position || a.id - b.id
}

// Categories without a published article anywhere below them are left out, so
// the navigation never leads to an empty page. A category whose parent is
// missing from the list is shown at the top level instead of being lost.
export function buildCategoryTree(portal: HelpPortalIndex): HelpCategoryNode[] {
  const nodes = new Map<number, HelpCategoryNode>()
  for (const category of portal.categories) {
    nodes.set(category.id, {
      ...category,
      children: [],
      articles: [],
      articleCount: 0,
    })
  }
  for (const article of portal.articles) {
    if (article.categoryId != null) {
      nodes.get(article.categoryId)?.articles.push(article)
    }
  }
  const roots: HelpCategoryNode[] = []
  for (const node of nodes.values()) {
    const parent = node.parentId != null ? nodes.get(node.parentId) : undefined
    if (parent && parent !== node) parent.children.push(node)
    else roots.push(node)
  }
  const finish = (node: HelpCategoryNode, seen: Set<number>): number => {
    if (seen.has(node.id)) return 0
    seen.add(node.id)
    node.articles.sort(byPosition)
    node.children = node.children
      .filter((child) => finish(child, seen) > 0)
      .sort(byPosition)
    node.articleCount =
      node.articles.length +
      node.children.reduce((sum, child) => sum + child.articleCount, 0)
    return node.articleCount
  }
  const seen = new Set<number>()
  return roots.filter((root) => finish(root, seen) > 0).sort(byPosition)
}

export function findCategory(
  tree: HelpCategoryNode[],
  categoryId: number
): HelpCategoryNode | undefined {
  for (const node of tree) {
    if (node.id === categoryId) return node
    const nested = findCategory(node.children, categoryId)
    if (nested) return nested
  }
  return undefined
}

// The chain of categories from the top level down to `categoryId`.
export function categoryTrail(
  tree: HelpCategoryNode[],
  categoryId: number | null
): HelpCategoryNode[] {
  if (categoryId == null) return []
  for (const node of tree) {
    if (node.id === categoryId) return [node]
    const nested = categoryTrail(node.children, categoryId)
    if (nested.length) return [node, ...nested]
  }
  return []
}

export function articlesWithoutCategory(
  portal: HelpPortalIndex
): HelpArticleSummary[] {
  const known = new Set(portal.categories.map((category) => category.id))
  return portal.articles
    .filter(
      (article) => article.categoryId == null || !known.has(article.categoryId)
    )
    .sort(byPosition)
}

const LEGACY_ARTICLE_RE =
  /^https?:\/\/hilfe\.cleverlohn\.de\/[a-z-]+\/articles\/(\d+)/i

// Articles imported from the old help center keep its numeric id at the start
// of their slug, so a link written for the old site still finds its article.
export function resolveLegacyHelpLink(
  url: string | null | undefined,
  index: HelpIndex | null | undefined
): { path: string; hash: string } | null {
  if (!url || !index) return null
  const match = url.match(LEGACY_ARTICLE_RE)
  if (!match) return null
  const prefix = `${match[1]}-`
  for (const portal of index.portals) {
    const article = portal.articles.find(
      (candidate) =>
        candidate.slug === match[1] || candidate.slug.startsWith(prefix)
    )
    if (article) {
      const hashAt = url.indexOf('#')
      return {
        path: helpArticlePath(portal.portal, article.id),
        hash: hashAt >= 0 ? url.slice(hashAt) : '',
      }
    }
  }
  return null
}

const LEGACY_HOST_RE = /^https?:\/\/hilfe\.cleverlohn\.de(?:[/?#]|$)/i
const LEGACY_COLLECTION_RE =
  /^https?:\/\/hilfe\.cleverlohn\.de\/[a-z-]+\/collections\/(\d+)/i

// Old help center collections the app links to, by the slug of the category
// that replaced them. Categories did not keep the old id in their slug.
const LEGACY_COLLECTION_SLUGS: Record<string, string> = {
  '19773901': 'fuer-unternehmen-datenbereinigung',
  '5559553': 'fuer-kanzleien-lohnbuchhalter-innen',
}

export function helpLegacyArticlePath(legacyId: string) {
  return `${HELP_ROOT}/l/${legacyId}`
}

export function helpLegacyCollectionPath(legacyId: string) {
  return `${HELP_ROOT}/l/c/${legacyId}`
}

// The in-app address for a link written for the old help center, or null when
// the URL is not one of its links. The article itself is looked up when the
// address is opened, because article ids differ between environments.
export function legacyHelpPath(url: string | null | undefined): string | null {
  if (!url || !LEGACY_HOST_RE.test(url)) return null
  const article = url.match(LEGACY_ARTICLE_RE)
  if (article) return helpLegacyArticlePath(article[1])
  const collection = url.match(LEGACY_COLLECTION_RE)
  if (collection) return helpLegacyCollectionPath(collection[1])
  return HELP_ROOT
}

export function onboardingHelpArticlePath(companyUid: string, ref: string) {
  return `/onboarding/${companyUid}/help/a/${ref}`
}

export function onboardingHelpCollectionPath(companyUid: string, slug: string) {
  return `/onboarding/${companyUid}/help/c/${slug}`
}

// The onboarding pages have their own reader for the guides, because they are
// used without a login. Null when the URL is not an old help center article or
// one of the collections the app links to.
export function onboardingHelpPath(
  companyUid: string,
  url: string | null | undefined
): string | null {
  if (!url || !LEGACY_HOST_RE.test(url)) return null
  const article = url.match(LEGACY_ARTICLE_RE)
  if (article) return onboardingHelpArticlePath(companyUid, article[1])
  const collection = url.match(LEGACY_COLLECTION_RE)
  const slug = collection ? LEGACY_COLLECTION_SLUGS[collection[1]] : undefined
  return slug ? onboardingHelpCollectionPath(companyUid, slug) : null
}

export function findLegacyArticle(
  legacyId: string,
  index: HelpIndex
): { portal: HelpPortalKey; id: number } | null {
  const prefix = `${legacyId}-`
  for (const portal of index.portals) {
    const article = portal.articles.find(
      (candidate) =>
        candidate.slug === legacyId || candidate.slug.startsWith(prefix)
    )
    if (article) return { portal: portal.portal, id: article.id }
  }
  return null
}

export function findLegacyCollection(
  legacyId: string,
  index: HelpIndex
): { portal: HelpPortalKey; id: number } | null {
  const slug = LEGACY_COLLECTION_SLUGS[legacyId]
  if (!slug) return null
  for (const portal of index.portals) {
    const category = portal.categories.find((entry) => entry.slug === slug)
    if (category) return { portal: portal.portal, id: category.id }
  }
  return null
}

export function helpLocale(language: string | undefined): 'de' | 'en' {
  return language?.toLowerCase().startsWith('en') ? 'en' : 'de'
}

export interface HighlightPart {
  text: string
  match: boolean
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// Splits `text` so the words of `query` can be shown in bold, as the old help
// center's search results did.
export function highlightParts(text: string, query: string): HighlightPart[] {
  const terms = query
    .trim()
    .split(/\s+/)
    .filter((term) => term.length > 0)
    .map(escapeRegExp)
  if (!text || terms.length === 0) return text ? [{ text, match: false }] : []
  const pattern = new RegExp(`(${terms.join('|')})`, 'gi')
  return text
    .split(pattern)
    .map((part, index) => ({ text: part, match: index % 2 === 1 }))
    .filter((part) => part.text.length > 0)
}
